import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  MoreVertical,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useNotification } from '@/components/notifications';
import { getWorkflowList } from '@/services/workflow/get-workflow-list';
import { useListTriggerSubscriptions } from '@/hooks/queries/triggers/use-list-trigger-subscriptions';
import {
  useCreateTriggerSubscription,
  useDeleteTriggerSubscription,
  useSetTriggerEnabled,
  useTestFireTrigger,
} from '@/hooks/mutations/triggers/use-trigger-mutations';
import { Trigger } from '@/services/triggers/types';
import { useWorkflowFileInputs } from '@/hooks/queries/triggers/use-workflow-file-inputs';
import {
  FileInputSelect,
  buildInputBinding,
  optionKey,
} from '@/pages/triggers/file-input-select';
import { groupLabelsForMimeTypes } from './file-type-groups';

interface TriggerRowProps {
  trigger: Trigger;
  orgId?: string;
  onRequestDelete: (trigger: Trigger) => void;
}

export const TriggerRow = ({
  trigger,
  orgId,
  onRequestDelete,
}: TriggerRowProps) => {
  const notification = useNotification();
  const [expanded, setExpanded] = useState(false);
  const [workflowToAdd, setWorkflowToAdd] = useState('');
  const [fileInputToAdd, setFileInputToAdd] = useState('');

  // Only asked for when the chosen workflow has more than one file input.
  const fileInputsQuery = useWorkflowFileInputs(orgId, workflowToAdd);
  const fileInputs = fileInputsQuery.data ?? [];
  const needsFileInputChoice = fileInputs.length > 1;
  // Attaching on an unknown shape would fall back to auto-bind and be rejected.
  const inputsUnknown =
    !!workflowToAdd && (fileInputsQuery.isPending || fileInputsQuery.isError);
  const hasNoFileInput =
    !!workflowToAdd && !inputsUnknown && !fileInputs.length;

  const setEnabled = useSetTriggerEnabled(orgId);
  const testFire = useTestFireTrigger(orgId);
  const addSubscription = useCreateTriggerSubscription(orgId);
  const removeSubscription = useDeleteTriggerSubscription(orgId);

  const subscriptionsQuery = useListTriggerSubscriptions(
    orgId,
    expanded ? trigger.id : undefined,
  );
  const subscriptions = useMemo(
    () => subscriptionsQuery.data ?? [],
    [subscriptionsQuery.data],
  );

  const workflowsQuery = useQuery({
    queryKey: ['triggerable-workflows', orgId],
    queryFn: () => getWorkflowList(orgId!),
    enabled: !!orgId && expanded,
    staleTime: 60_000,
  });

  const workflowNames = useMemo(() => {
    const map = new Map<string, string>();
    (workflowsQuery.data?.data?.workflows ?? []).forEach((w) =>
      map.set(w.workflow_id, w.name ?? w.workflow_id),
    );
    return map;
  }, [workflowsQuery.data]);

  // Which workflows are already bound is only known once the subscriptions have
  // loaded. Offering the list before then shows attached workflows as
  // selectable, and a second binding to the same workflow runs it twice for
  // every file — session ids are per (trigger, subscription, file), so nothing
  // downstream collapses the duplicate.
  const bindingsUnknown =
    subscriptionsQuery.isPending || subscriptionsQuery.isError;

  const attachable = useMemo(() => {
    if (bindingsUnknown) return [];
    const attached = new Set(subscriptions.map((s) => s.target_ref));
    return (workflowsQuery.data?.data?.workflows ?? []).filter(
      (w) =>
        !attached.has(w.workflow_id) &&
        (w.user_role === 'workflow:owner' || w.user_role === 'workflow:editor'),
    );
  }, [workflowsQuery.data, subscriptions, bindingsUnknown]);

  // null when the API redacted it (someone else's Drive). This row only renders
  // for connections the caller owns, so that is a defensive fallback, not a case
  // the Triggers page is expected to hit.
  const config = trigger.config ?? {};

  const folderLabel =
    (config.folder_name as string | undefined) ||
    trigger.name ||
    (config.folder_id as string | undefined) ||
    'Unnamed folder';

  const fileTypeLabels = groupLabelsForMimeTypes(config.filters?.mime_types);

  const handleToggle = async (enabled: boolean) => {
    try {
      await setEnabled.mutateAsync({ triggerId: trigger.id, enabled });
      notification.success?.(enabled ? 'Trigger enabled' : 'Trigger paused');
    } catch (error) {
      Sentry.captureException(error, { extra: { triggerId: trigger.id } });
      notification.error(
        error instanceof Error ? error.message : 'Could not update the trigger',
      );
    }
  };

  const handleTestFire = async () => {
    try {
      const result = await testFire.mutateAsync(trigger.id);
      const detected = Number(result.stats?.detected ?? 0);
      notification.success?.(
        detected > 0
          ? `Found ${detected} new file(s) and started run(s)`
          : 'No new files found in the folder',
      );
    } catch (error) {
      Sentry.captureException(error, { extra: { triggerId: trigger.id } });
      notification.error(
        error instanceof Error ? error.message : 'Test run failed',
      );
    }
  };

  const handleAddWorkflow = async () => {
    if (!workflowToAdd) return;
    try {
      const chosen = fileInputs.find((i) => optionKey(i) === fileInputToAdd);
      await addSubscription.mutateAsync({
        triggerId: trigger.id,
        input: {
          target_ref: workflowToAdd,
          // Omitted for a single file input so the backend auto-binds it.
          input_binding: buildInputBinding(
            needsFileInputChoice ? chosen : undefined,
          ),
        },
      });
      setWorkflowToAdd('');
      setFileInputToAdd('');
      notification.success?.('Workflow attached');
    } catch (error) {
      Sentry.captureException(error, {
        extra: { triggerId: trigger.id, workflowId: workflowToAdd },
      });
      notification.error(
        error instanceof Error
          ? error.message
          : 'Could not attach the workflow',
      );
    }
  };

  const handleRemoveWorkflow = async (subscriptionId: string) => {
    try {
      await removeSubscription.mutateAsync({
        triggerId: trigger.id,
        subscriptionId,
      });
      notification.success?.('Workflow detached');
    } catch (error) {
      Sentry.captureException(error, {
        extra: { triggerId: trigger.id, subscriptionId },
      });
      notification.error(
        error instanceof Error
          ? error.message
          : 'Could not detach the workflow',
      );
    }
  };

  return (
    <div className='rounded-lg border'>
      <div className='flex items-center gap-3 p-3'>
        <Button
          variant='ghost'
          size='icon'
          className='size-6 shrink-0'
          onClick={() => setExpanded((prev) => !prev)}
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? (
            <ChevronDown className='size-4' />
          ) : (
            <ChevronRight className='size-4' />
          )}
        </Button>

        <div className='min-w-0 flex-1'>
          <div className='flex items-center gap-2'>
            <span className='truncate font-medium'>{folderLabel}</span>
            {!trigger.enabled && <Badge variant='secondary'>Paused</Badge>}
            {trigger.failure_count > 0 && (
              <Badge variant='destructive'>
                {trigger.failure_count} recent failure(s)
              </Badge>
            )}
          </div>
          <p className='truncate text-xs text-muted-foreground'>
            {fileTypeLabels.length
              ? fileTypeLabels.join(', ')
              : 'All file types'}
            {' · checks every '}
            {Number(config.poll_interval_seconds ?? 60) / 60} min
            {trigger.last_polled_at
              ? ` · last checked ${new Date(trigger.last_polled_at).toLocaleString()}`
              : ' · not checked yet'}
          </p>
        </div>

        <Switch
          checked={trigger.enabled}
          onCheckedChange={handleToggle}
          disabled={setEnabled.isPending}
          aria-label='Enable trigger'
        />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='size-8'
              aria-label='Trigger actions'
            >
              <MoreVertical className='size-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuItem
              onClick={handleTestFire}
              disabled={testFire.isPending}
            >
              {testFire.isPending && (
                <Loader2 className='mr-2 size-4 animate-spin' />
              )}
              Check for files now
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onRequestDelete(trigger)}>
              Delete trigger
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {expanded && (
        <div className='space-y-3 border-t px-3 py-3 pl-12'>
          <p className='text-xs font-medium text-muted-foreground'>
            Workflows started by this folder
          </p>

          {subscriptionsQuery.isLoading ? (
            <Loader2 className='size-4 animate-spin' />
          ) : (
            <div className='space-y-1'>
              {subscriptions.length === 0 && (
                <p className='text-sm text-muted-foreground'>
                  No workflows attached — this trigger will detect files but not
                  run anything.
                </p>
              )}
              {subscriptions.map((sub) => (
                <div
                  key={sub.id}
                  className='flex items-center justify-between rounded-md bg-muted/50 px-2 py-1.5'
                >
                  <span className='truncate text-sm'>
                    {workflowNames.get(sub.target_ref) ?? sub.target_ref}
                  </span>
                  <Button
                    variant='ghost'
                    size='icon'
                    className='size-6'
                    onClick={() => handleRemoveWorkflow(sub.id)}
                    disabled={removeSubscription.isPending}
                    aria-label='Detach workflow'
                  >
                    <X className='size-3.5' />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className='space-y-2'>
            <div className='flex items-end gap-2'>
              <div className='flex-1'>
                <Select
                  value={workflowToAdd}
                  disabled={bindingsUnknown}
                  onValueChange={(next) => {
                    setWorkflowToAdd(next);
                    setFileInputToAdd('');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        bindingsUnknown
                          ? 'Loading attached workflows…'
                          : 'Attach another workflow'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {attachable.map((w) => (
                      <SelectItem key={w.workflow_id} value={w.workflow_id}>
                        {w.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant='outline'
                onClick={handleAddWorkflow}
                disabled={
                  !workflowToAdd ||
                  addSubscription.isPending ||
                  bindingsUnknown ||
                  inputsUnknown ||
                  hasNoFileInput ||
                  (needsFileInputChoice && !fileInputToAdd)
                }
              >
                {addSubscription.isPending && (
                  <Loader2 className='mr-2 size-4 animate-spin' />
                )}
                Attach
              </Button>
              {!!workflowToAdd && (
                <Button
                  variant='ghost'
                  onClick={() => {
                    setWorkflowToAdd('');
                    setFileInputToAdd('');
                  }}
                  disabled={addSubscription.isPending}
                >
                  Cancel
                </Button>
              )}
            </div>

            {needsFileInputChoice && (
              <FileInputSelect
                options={fileInputs}
                value={fileInputToAdd}
                onChange={setFileInputToAdd}
                disabled={addSubscription.isPending}
              />
            )}
            {hasNoFileInput && (
              <p className='text-xs text-destructive'>
                This workflow has no file input, so there is nowhere to deliver
                the file. Add a file input node to it first.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
